import React, { useState } from "react"
import {
  Box, Select as ChakraSelect, Stack, Text,
} from "@chakra-ui/react"
import Modal from "components/dialogs/Modal"
import Button from "components/Buttons/Button"
import CancelButton from "components/Buttons/CancelButton"
import UpgradeAlert from "components/elements/UpgradeAlert"
import { useCurrentEvent } from "queries/events"
import { useLanguageTemplates } from "queries/languageTemplates"

type Props = {
  eventId: string
  currentLanguageTemplateId: string | null
  onClose: () => void
  onSave: (languageTemplateId: string | null) => void | Promise<void>
}

const FieldLabel = ({ title, caption }: { title: string, caption: string }) => (
  <Box>
    <Text fontWeight="bold">{title}</Text>
    <Text fontSize="sm" color="gray.600" mt={1}>{caption}</Text>
  </Box>
)

const FormLanguageModal = ({
  eventId, currentLanguageTemplateId, onClose, onSave,
}: Props) => {
  const { data: event } = useCurrentEvent()
  const { data: templates = [] } = useLanguageTemplates(eventId)
  const { customLanguageTemplatesEnabled } = event
  const [languageTemplateId, setLanguageTemplateId] = useState(currentLanguageTemplateId ?? "")
  const [isSaving, setIsSaving] = useState(false)

  const defaultTemplateName = templates.find(
    (template) => template.id === event.defaultLanguageTemplateId,
  )?.name ?? "English"

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await onSave(languageTemplateId || null)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      show
      hide={onClose}
      title="Form Language"
      size="xl"
      actions={[
        <CancelButton onClick={onClose} isDisabled={isSaving} />,
        <Button onClick={handleSave} isLoading={isSaving}>Save</Button>,
      ]}
    >
      <Stack spacing={6}>
        <Text>
          Select the language template used for this form. Templates control guest-facing
          text, including field labels, buttons, terminology, and messages.
        </Text>
        <Box display="grid" gridTemplateColumns="1fr 1fr" rowGap={6} columnGap={4} alignItems="center">
          <FieldLabel title="Event Language" caption="Managed in Event Settings." />
          <ChakraSelect isDisabled value={defaultTemplateName} bg="white">
            <option value={defaultTemplateName}>{defaultTemplateName}</option>
          </ChakraSelect>
          <FieldLabel title="Form Language" caption="Select a language template for this form." />
          <ChakraSelect
            value={languageTemplateId}
            onChange={(event_) => setLanguageTemplateId(event_.target.value)}
            isDisabled={!customLanguageTemplatesEnabled}
            bg="white"
          >
            <option value="">{`Event Default (${defaultTemplateName})`}</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>{template.name}</option>
            ))}
          </ChakraSelect>
        </Box>
        {customLanguageTemplatesEnabled ? (
          <Text>Customize templates in Event Settings → Language Templates.</Text>
        ) : (
          <UpgradeAlert text="Form-specific languages are available with a Corporate plan or higher." />
        )}
      </Stack>
    </Modal>
  )
}

export default FormLanguageModal
